import { useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, MouseEvent as ReactMouseEvent, ReactElement, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X } from "lucide-react";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import {
  buildPickerIndex,
  firstHighlight,
  isOptionSelected,
  isSelectable,
  moveHighlight,
  pickerLabel,
  pickerOptions,
  pickerPanel,
  pickerSelection,
  toggleId,
  type PickerIndex,
  type PickerPanel,
  type PickerSelection,
  type SelectableOption,
} from "./DestinationPicker.model";
import type { DestinationPickerProps } from "./DestinationPicker.types";

/**
 * Sdílený výběr destinace (rozhraní a háčky E2E v `DestinationPicker.types.ts`, logika
 * v `DestinationPicker.model.ts`). Data čte z `useAdminDestinationList`
 * (`GET /api/admin/destinations?fields=slim`, sdílená keš seznamu); vlastní dotaz nemá.
 *
 * Formulář výběr váže přes `<Controller>` v souboru editoru, nikdy propem `control`/`register`
 * (měřidlo nápověd, pravidlo `vazba-mimo-editor`). Výběr leží uvnitř `<form>` editoru, proto má
 * každé tlačítko `type="button"` a Enter v poli hledání volá `preventDefault`. `ref` míří na
 * spouštěč, aby formulář při neplatném odeslání fokusoval pole (`field.ref` z `Controller`).
 */
export default function DestinationPicker({ ref, ...props }: DestinationPickerProps): ReactElement {
  const list = useAdminDestinationList();
  const rows = list.data?.data;
  const index = useMemo(() => buildPickerIndex(rows ?? []), [rows]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const uid = useId();

  useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement, []);

  const selection = pickerSelection(props);
  const optionsFor = (text: string) =>
    pickerOptions(index, {
      query: text,
      level: props.level,
      excludeIds: selection.excludeIds,
      allowNone: selection.allowNone,
    });
  // Volby jen u otevřeného panelu: zavřený výběr ukazuje jen popisek hodnoty a stav dat.
  const options = open ? optionsFor(query) : [];
  const panel = pickerPanel({
    hasData: rows !== undefined,
    isError: list.isError,
    isFetching: list.isFetching,
    options,
    onRetry: () => void list.refetch(),
  });

  const close = (focusTrigger: boolean) => {
    setOpen(false);
    setQuery("");
    setHighlight(-1);
    if (focusTrigger) triggerRef.current?.focus();
  };

  const openPanel = () => {
    if (props.disabled) return;
    setQuery("");
    setHighlight(firstHighlight(optionsFor(""), selection));
    setOpen(true);
  };

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
        setHighlight(-1);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open || highlight < 0) return;
    document.getElementById(optionDomId(uid, highlight))?.scrollIntoView({ block: "nearest" });
  }, [open, highlight, uid]);

  const toggle = (id: string) => {
    if (props.mode === "multiple") props.onChange(toggleId(props.value, id));
  };

  const select = (option: SelectableOption) => {
    if (props.mode === "multiple") {
      if (option.kind === "destination") toggle(option.id);
      return;
    }
    props.onChange(option.kind === "none" ? null : option.id);
    close(true);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const visible = panel.kind === "options" ? panel.options : [];
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        setHighlight((h) => moveHighlight(h, event.key === "ArrowDown" ? 1 : -1, visible));
        return;
      case "Enter": {
        event.preventDefault();
        const option = visible[highlight];
        if (option && isSelectable(option)) select(option);
        return;
      }
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close(true);
        return;
      case "Tab":
        close(false);
        return;
    }
  };

  return (
    <DestinationPickerView
      selection={selection}
      attrs={props}
      index={index}
      panel={panel}
      open={open}
      query={query}
      highlight={highlight}
      uid={uid}
      rootRef={rootRef}
      triggerRef={triggerRef}
      inputRef={inputRef}
      onToggle={() => (open ? close(false) : openPanel())}
      onTriggerKeyDown={(event) => {
        if (event.key === "ArrowDown" && !open) {
          event.preventDefault();
          openPanel();
        }
      }}
      onQueryChange={(text) => {
        setQuery(text);
        setHighlight(firstHighlight(optionsFor(text)));
      }}
      onInputKeyDown={onInputKeyDown}
      onHighlight={setHighlight}
      onSelect={select}
      onRemove={toggle}
    />
  );
}

const optionDomId = (uid: string, i: number) => `${uid}-option-${i}`;

/** Atributy spouštěče z veřejných props (bez hodnoty a `onChange`, ty patří kontejneru). */
type PickerAttrs = Pick<DestinationPickerProps, "id" | "disabled" | "invalid" | "aria-describedby">;

export interface DestinationPickerViewProps {
  /** Normalizovaný výběr (`pickerSelection`). */
  selection: PickerSelection;
  attrs: PickerAttrs;
  index: PickerIndex;
  panel: PickerPanel;
  open: boolean;
  query: string;
  /** Index zvýrazněné volby v `panel.options`; `-1` = žádná. */
  highlight: number;
  /** Základ ID prvků (listbox, volby). */
  uid: string;
  rootRef?: RefObject<HTMLDivElement | null>;
  triggerRef?: RefObject<HTMLButtonElement | null>;
  inputRef?: RefObject<HTMLInputElement | null>;
  onToggle: () => void;
  onTriggerKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  onQueryChange: (query: string) => void;
  onInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onHighlight: (index: number) => void;
  onSelect: (option: SelectableOption) => void;
  onRemove: (id: string) => void;
}

/** Štítek „koncept" u nepublikované destinace. */
function DraftBadge({ text }: { text: string }) {
  return (
    <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-normal text-gray-500 dark:bg-gray-700 dark:text-gray-400">
      {text}
    </span>
  );
}

/**
 * Prezentační část výběru: řízená, bez dat a bez vlastního stavu (testuje se statickým
 * renderem, projekt vitest `admin` DOM nemá). Texty jen přes `t("shared.destinationPicker.…")`.
 */
export function DestinationPickerView({
  rootRef,
  triggerRef,
  inputRef,
  ...props
}: DestinationPickerViewProps): ReactElement {
  const { t } = useTranslation();
  const { selection, attrs, index, panel, open, query, highlight, uid } = props;
  const { multi, allowNone } = selection;
  const listboxId = `${uid}-listbox`;
  const placeholder = t("shared.destinationPicker.searchPlaceholder");
  const excluded = new Set(selection.excludeIds);
  const selectedIds = [...selection.selectedIds];
  const singleValue = multi ? null : (selectedIds[0] ?? null);

  const triggerContent = (): ReactElement => {
    if (singleValue) {
      const label = pickerLabel(index, singleValue);
      if (label)
        return (
          <span className="truncate">
            {label.text}
            {label.isDraft && <DraftBadge text={t("shared.destinationPicker.draft")} />}
          </span>
        );
      if (panel.kind === "loading")
        return <span className="text-gray-400">{t("shared.destinationPicker.loading")}</span>;
      if (panel.kind === "error")
        return <span className="text-red-600">{t("shared.destinationPicker.loadError")}</span>;
      return <span className="truncate">{singleValue}</span>;
    }
    return (
      <span className="text-gray-400">
        {allowNone ? t("shared.destinationPicker.none") : placeholder}
      </span>
    );
  };

  return (
    <div
      ref={rootRef}
      data-destination-picker=""
      data-mode={multi ? "multiple" : "single"}
      className="relative"
    >
      {multi && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedIds
            .filter((id) => !excluded.has(id))
            .map((id) => {
              const label = pickerLabel(index, id);
              return (
                <span
                  key={id}
                  data-destination-tag=""
                  className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
                >
                  {label?.text ?? id}
                  {label?.isDraft && <DraftBadge text={t("shared.destinationPicker.draft")} />}
                  <button
                    type="button"
                    aria-label={t("shared.destinationPicker.removeTag")}
                    title={t("shared.destinationPicker.removeTag")}
                    disabled={attrs.disabled}
                    onClick={() => props.onRemove(id)}
                    className="hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
        </div>
      )}

      <button
        type="button"
        ref={triggerRef}
        id={attrs.id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-describedby={attrs["aria-describedby"]}
        disabled={attrs.disabled}
        onClick={props.onToggle}
        onKeyDown={props.onTriggerKeyDown}
        className={`input flex items-center justify-between gap-2 text-left ${attrs.invalid ? "input-error" : ""}`}
      >
        {multi ? <span className="text-gray-400">{placeholder}</span> : triggerContent()}
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              panel.kind === "options" && highlight >= 0 ? optionDomId(uid, highlight) : undefined
            }
            aria-label={placeholder}
            placeholder={placeholder}
            value={query}
            onChange={(event) => props.onQueryChange(event.target.value)}
            onKeyDown={props.onInputKeyDown}
            className="input mb-2"
          />
          {panel.kind === "loading" && (
            <p role="status" className="py-3 text-center text-sm text-gray-500">
              {t("shared.destinationPicker.loading")}
            </p>
          )}
          {panel.kind === "error" && (
            <div role="alert" className="space-y-2 py-3 text-center text-sm">
              <p className="text-red-600">{t("shared.destinationPicker.loadError")}</p>
              <button type="button" onClick={panel.retry} className="btn-secondary">
                {t("shared.destinationPicker.retry")}
              </button>
            </div>
          )}
          {panel.kind === "empty" && (
            <p className="py-3 text-center text-sm text-gray-500">
              {t("shared.destinationPicker.noMatches")}
            </p>
          )}
          {panel.kind === "options" && (
            <ul
              id={listboxId}
              role="listbox"
              aria-multiselectable={multi}
              className="max-h-80 overflow-y-auto"
            >
              {panel.options.map((option, i) => {
                if (option.kind === "heading")
                  return (
                    <li
                      key={option.id}
                      role="presentation"
                      className="px-3 py-1.5 text-sm font-semibold text-gray-400"
                    >
                      {option.name}
                    </li>
                  );
                const selected = isOptionSelected(option, selection);
                const tone = i === highlight ? "bg-primary-50 dark:bg-primary-900/30" : "";
                const common = {
                  id: optionDomId(uid, i),
                  role: "option",
                  "aria-selected": selected,
                  onMouseDown: (event: ReactMouseEvent) => event.preventDefault(),
                  onMouseMove: () => i !== highlight && props.onHighlight(i),
                  onClick: () => props.onSelect(option),
                };
                const check = selected && multi && <span className="text-primary-600 ml-2">✓</span>;
                if (option.kind === "none")
                  return (
                    <li
                      key="none"
                      {...common}
                      data-none=""
                      className={`cursor-pointer rounded px-3 py-1.5 text-sm ${tone}`}
                    >
                      <span className="text-gray-500">{t("shared.destinationPicker.none")}</span>
                      {check}
                    </li>
                  );
                const weight = option.level === "country" ? "font-semibold" : "";
                const pad = option.indent ? "pl-6" : "";
                return (
                  <li
                    key={option.id}
                    {...common}
                    data-level={option.level}
                    className={`cursor-pointer rounded px-3 py-1.5 text-sm ${tone} ${weight} ${pad}`}
                  >
                    {option.indent ? option.name : option.label}
                    {option.isDraft && <DraftBadge text={t("shared.destinationPicker.draft")} />}
                    {check}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
